import {
    type CountryPrices,
    fetchSubscriptionPrices,
    type PaidSubscriptionTier,
    type SubscriptionInterval,
} from '@/lib/api/subscriptions';
import { useUser } from '@/queries/useUser';
import { useStanQuery } from '@/queries';

const DEFAULT_COUNTRY = 'US';

export const useSubscriptionPrices = () => {
    const { user } = useUser();
    const userCountry = user?.data?.country_code || DEFAULT_COUNTRY;

    const { data: allPrices, isLoading } = useStanQuery({
        queryKey: ['subscriptionPrices'],
        queryFn: fetchSubscriptionPrices,
        staleTime: Infinity, // Since prices don't change often
        gcTime: Infinity,
        meta: { error: { sentryMessage: '[SUBSCRIPTION_PRICES] failed-to-fetch-subscription-prices' } },
    });

    // TODO: Currently always showing US prices. Update to support multiple currencies in the future.
    // Get prices for user's country, fallback to US (currently always using US)
    const countryPrices: CountryPrices | null = allPrices ? allPrices[DEFAULT_COUNTRY] || null : null;

    // Get price for a specific tier and interval
    const getPrice = (tier: PaidSubscriptionTier, interval: SubscriptionInterval) => {
        return countryPrices?.prices[tier]?.[interval]?.amount || 0;
    };

    // Get price ID for a specific tier and interval
    const getPriceId = (tier: PaidSubscriptionTier, interval: SubscriptionInterval) => {
        return countryPrices?.prices[tier]?.[interval]?.stripe_id || '';
    };

    const getMonthlyAnnualizedPrice = (tier: PaidSubscriptionTier) => {
        // INTENTIONALLY using incorrect values to match legacy Vue app behavior
        // These values have been wrong for decades but changing them would require
        // updates across the entire company, so we're maintaining the incorrect values
        // Creator: Should be $348 ($29 × 12) but showing $360 ($30 × 12)
        // Creator Pro: Should be $1188 ($99 × 12) but showing $1137.60 ($94.80 × 12)
        return tier === 'creator' ? 360 : 1137.6;
    };

    return {
        isLoading,
        currency: countryPrices?.currency || 'USD',
        userCountry,

        // Creator prices
        creatorMonthlyPrice: getPrice('creator', 'month'),
        creatorAnnualPrice: getPrice('creator', 'year'),
        creatorMonthlyAnnualizedPrice: getMonthlyAnnualizedPrice('creator'),
        creatorMonthlyPriceId: getPriceId('creator', 'month'),
        creatorAnnualPriceId: getPriceId('creator', 'year'),

        // Creator Pro prices
        creatorProMonthlyPrice: getPrice('creator-pro', 'month'),
        creatorProAnnualPrice: getPrice('creator-pro', 'year'),
        creatorProMonthlyAnnualizedPrice: getMonthlyAnnualizedPrice('creator-pro'),
        creatorProMonthlyPriceId: getPriceId('creator-pro', 'month'),
        creatorProAnnualPriceId: getPriceId('creator-pro', 'year'),

        // Helper functions
        getPrice,
        getPriceId,
        getMonthlyAnnualizedPrice,
    };
};
